import { html, type TemplateResult } from 'lit-html';
import { app, type Page } from '../app.js';
import { statusPill } from '../format.js';

/**
 * In-app guide: everything a dialer-ops admin or supervisor needs to run ACW Enforcer
 * without asking. Keep it in step with the UI labels it quotes (button and field names).
 */

interface Section {
  id: string;
  title: string;
  body: () => TemplateResult;
}

const b = (s: string) => html`<b class="ui">${s}</b>`;
const go = (route: string, label: string) => html`<a href="#/${route}">${label}</a>`;
const see = (id: string, label: string) => html`<a href="#/guide/${id}">${label}</a>`;
const adminOnly = html`<span class="pill neutral">Admins only</span>`;

const SECTIONS: Section[] = [
  {
    id: 'start',
    title: 'Start here: what ACW Enforcer does',
    body: () => html`
      <p class="lead">When an agent doesn't finish After Call Work within the queue's time limit (often 2 minutes; ${go('reports', 'Reports')}
        shows each queue's limit), Genesys gives the interaction the wrap-up code <code>ININ-WRAP-UP-TIMEOUT</code>. ACW Enforcer sees that within seconds and, if the rules say so, takes the agent
        <b>off that one queue</b> until they turn it back on themselves.</p>
      <ol class="flow">
        <li>Agent misses the ACW time limit</li>
        <li>Genesys assigns <code>ININ-WRAP-UP-TIMEOUT</code></li>
        <li>ACW Enforcer checks the rules</li>
        <li class="accent">Agent deactivated on <b>that queue only</b></li>
        <li>Agent reactivates the queue in Genesys</li>
        <li>Time off the queue is recorded</li>
      </ol>
      <h3>What it never does</h3>
      <ul>
        <li>It never removes anyone from a queue. They stay a member; only "active on this queue" is switched off.</li>
        <li>It never changes anyone's status or presence (Available, Off Queue, Break…).</li>
        <li>It never touches the agent's other queues.</li>
        <li>It never turns a queue back on. The agent (or a supervisor in Genesys) does that.</li>
        <li>It never reacts to anything except that exact wrap-up code: not disconnects, not long ACW on its own.</li>
      </ul>
      <h3>Who can do what</h3>
      <p><b>Viewers</b> can see every page. <b>Admins</b> can also change queues, rules, exemptions and users, and use
        ${b('Emergency stop')}. <b>Owners</b> (super admins) can also manage other owners. Click your name at the top right to see your role, pick a light, dark or system theme, or sign out. See ${see('users', 'Users and access')}.</p>`,
  },
  {
    id: 'dashboard',
    title: 'Reading the Dashboard',
    body: () => html`
      <p>The banner at the top tells you whether anyone can be deactivated right now:</p>
      <div class="table-wrap"><table>
        <thead><tr><th>Banner</th><th>Meaning</th></tr></thead>
        <tbody>
          <tr><td><b>Observe only</b></td><td>Nobody is deactivated. In <b>Detect</b> mode every timeout is recorded and reported. In <b>Capture</b> mode
            Genesys events are only stored, so the counts stay at zero.</td></tr>
          <tr><td><b>Dry run</b></td><td>Every decision is recorded as "would deactivate", nobody is deactivated.</td></tr>
          <tr><td><b>Live enforcement is switched off in the infrastructure</b></td><td>The deployment itself blocks deactivations. Only the system owner can change this (see ${see('safety', 'Safety switches')}).</td></tr>
          <tr><td><b>Enforcing live</b></td><td>Agents who time out on queues set to Enforce are deactivated.</td></tr>
        </tbody>
      </table></div>
      <h3>The cards</h3>
      <ul>
        <li><b>Worker:</b> Healthy means the service and its database are fine.</li>
        <li><b>Genesys:</b> Connected means it can talk to Genesys. "Not configured" or "Not signed in" → ${see('troubleshooting', 'Troubleshooting')}.</li>
        <li><b>Event feed:</b> when the last Genesys event arrived. During working hours this should be minutes ago, not hours.</li>
        <li><b>Failed messages:</b> should say None. Anything else → tell the system owner.</li>
      </ul>
      <p>Below them: timeouts in the last 24 hours and 7 days, deactivations in the last 7 days, how many agents are deactivated right now,
        and timeout records that failed in the last 24 hours (should be 0).</p>
      <p><b>Deactivated right now</b> lists agents waiting to reactivate a queue, and since when.
        <b>Latest timeouts</b> shows the newest timeouts and what happened. Click an agent's name for their report, or go to
        ${go('timeouts', 'Timeouts')} for the full list.</p>`,
  },
  {
    id: 'safety',
    title: 'The safety switches (and which one wins)',
    body: () => html`
      <p class="lead">A deactivation only happens when <b>every</b> switch below allows it. Any one of them can stop it.</p>
      <div class="table-wrap"><table>
        <thead><tr><th>Switch</th><th>Where</th><th>Who changes it</th></tr></thead>
        <tbody>
          <tr><td><b>Infrastructure switch</b> (live enforcement allowed)</td><td>The AWS deployment. Shown as a banner when off.</td><td>System owner only</td></tr>
          <tr><td><b>Worker mode</b>: Capture / Detect / Enforce</td><td>${go('rules', 'Rules')} › Enforcement</td><td>Admins</td></tr>
          <tr><td><b>Dry run everywhere</b></td><td>${go('rules', 'Rules')} › Enforcement (also set by ${b('Emergency stop')})</td><td>Admins</td></tr>
          <tr><td><b>Queue mode</b>: Off / Dry run / Enforce</td><td>${go('queues', 'Queues')}</td><td>Admins</td></tr>
        </tbody>
      </table></div>
      <h3>Worker modes</h3>
      <ul>
        <li><b>Capture:</b> setup only. Stores the raw Genesys events; no timeouts are counted, so the Dashboard and Reports stay at zero.</li>
        <li><b>Detect:</b> records every timeout and how long each wrap-up took, for the Dashboard and Reports. Never changes anything in Genesys.</li>
        <li><b>Enforce:</b> applies the rules to listed queues (still subject to dry run and the infrastructure switch).</li>
      </ul>
      <div class="callout info"><b>Good to know:</b> timeouts recorded while the worker wasn't in Enforce mode are never enforced later,
        even if you switch to Enforce a minute after.</div>`,
  },
  {
    id: 'emergency',
    title: 'Emergency stop',
    body: () => html`
      <p class="lead">Use it the moment something looks wrong, for example the wrong queue being enforced. ${adminOnly}</p>
      <ol class="steps">
        <li>Click ${b('Emergency stop')} in the top bar.</li>
        <li>Type a short reason (it goes in the audit log) and click ${b('Stop enforcing now')}.</li>
      </ol>
      <p>Within seconds the whole service is in dry run: nobody else is deactivated, including any deactivation that was waiting to retry.
        Agents who were already deactivated <b>stay</b> deactivated until they reactivate; the stop doesn't turn queues back on.</p>
      <h3>To resume</h3>
      <ol class="steps">
        <li>Open ${go('rules', 'Rules')}.</li>
        <li>Untick ${b('Dry run everywhere')} and click ${b('Save')}, then confirm.</li>
      </ol>`,
  },
  {
    id: 'queues',
    title: 'Managing queues',
    body: () => html`
      <p class="lead">Only queues on the ${go('queues', 'Queues')} page are managed. Everything else is ignored (recorded as "Queue not enforced"). ${adminOnly}</p>
      <h3>Add a queue</h3>
      <ol class="steps">
        <li>On ${go('queues', 'Queues')}, under <b>Add a queue</b>, type part of the Genesys queue name and click ${b('Search')}.
          For example, "Medicare" finds every queue with Medicare anywhere in its name.</li>
        <li>Click ${b('Add in dry run')} next to the right queue. New queues always start in dry run.</li>
      </ol>
      <h3>Change a queue's mode</h3>
      <p>Use the ${b('Off')} / ${b('Dry run')} / ${b('Enforce')} buttons on the queue's row. Choosing Enforce asks you to confirm.</p>
      <ul>
        <li><b>Off:</b> timeouts on this queue are recorded but never acted on.</li>
        <li><b>Dry run:</b> records "would deactivate" so you can check the results first.</li>
        <li><b>Enforce:</b> deactivates agents who time out on this queue.</li>
      </ul>
      <h3>Give one queue its own rules</h3>
      <p>Click ${b('Overrides')} on the queue's row to set a grace count, enforcement hours or a note for that queue only.
        Leave a field blank (or click ${b('Use the global hours')}) to follow the ${go('rules', 'Rules')} page. Click ${b('Save overrides')}.</p>
      <h3>Stop managing a queue</h3>
      <p>Click ${b('Remove')}. Agents already deactivated on it stay deactivated until they reactivate, and their reactivation is still recorded.</p>`,
  },
  {
    id: 'rollout',
    title: 'Rolling out a new queue safely',
    body: () => html`
      <p class="lead">Always try a queue in dry run for a few days before enforcing it.</p>
      <ol class="steps">
        <li>Check the queue's After Call Work in Genesys is <b>Mandatory, Time-boxed</b> (any time limit). Without a time limit, Genesys never assigns
          the timeout code. Once timeouts come in, ${go('reports', 'Reports')} › By queue shows the limit as <b>ACW limit</b>.</li>
        <li>Add the queue in dry run (see ${see('queues', 'Managing queues')}).</li>
        <li>Wait a few working days. On ${go('timeouts', 'Timeouts')}, filter <b>Outcome = Dry run</b> and check the agents and queue look right. Click a row: <b>Why</b> should say "would: deactivate".
          ${go('reports', 'Reports')} › By queue shows how many timeouts that queue has, so you know how many deactivations to expect.</li>
        <li>If you use a grace count, enforcement hours or exemptions, check those outcomes too (Grace warning, Outside hours, Exempt agent).</li>
        <li>Tell the agents on that queue what will happen and how to reactivate (see ${see('agents', 'When an agent asks')}).</li>
        <li>Switch the queue to ${b('Enforce')}.</li>
        <li>For the first day, watch <b>Deactivated right now</b> on the ${go('dashboard', 'Dashboard')}.</li>
      </ol>`,
  },
  {
    id: 'exemptions',
    title: 'Exempting an agent',
    body: () => html`
      <p class="lead">Exempt agents are never deactivated, for example supervisors or new hires in training. Their timeouts are still recorded and reported. ${adminOnly}</p>
      <ol class="steps">
        <li>Find the agent's Genesys user ID. Open their profile in Genesys Cloud; the ID is the long code (like <code>1a2b3c4d-…</code>) at the end of the page address.</li>
        <li>On ${go('exemptions', 'Exemptions')}, paste the ID, type a reason, and optionally pick an end date.</li>
        <li>Click ${b('Add')}. The agent's name appears once it's confirmed in Genesys.</li>
      </ol>
      <p>An exemption with an end date stops applying after that day. To end one early, click ${b('Remove')}.</p>`,
  },
  {
    id: 'rules',
    title: 'Rules: grace count, hours and advanced settings',
    body: () => html`
      <p class="lead">The ${go('rules', 'Rules')} page holds the global rules. A queue's ${b('Overrides')} beat these for that queue. Changes apply within seconds; click ${b('Save')}. ${adminOnly}</p>
      <h3>Grace count</h3>
      <p>"Deactivate on timeout number <b>N</b> within <b>D</b> days." With N = 1 every timeout is enforced. With N = 2, the first timeout in the
        window is recorded as a <b>Grace warning</b> and the second one is enforced. Counted per agent and queue, or per agent across all queues.
        Every recorded timeout counts, including ones on queues that aren't enforced.</p>
      <h3>Enforcement hours</h3>
      <ol class="steps">
        <li>Click ${b('Limit to set hours')}.</li>
        <li>Pick the time zone, the days, and the start and end time. ${b('Add window')} adds another set of days and times.</li>
        <li>Click ${b('Save')}.</li>
      </ol>
      <p>A timeout outside the hours is recorded as <b>Outside hours</b> and not enforced. The time that matters is when the timeout happened.
        A window whose end is earlier than its start runs past midnight (for example Fri 22:00 to 02:00). To go back to any time, click ${b('Enforce at any time')}.</p>
      <h3>Apply to every queue</h3>
      <p>Normally only listed queues are managed. ${b('Apply to every queue')} applies the chosen mode to all queues. Use with care.</p>
      <h3>Alarm emails</h3>
      <p>Who AWS emails when something needs attention: failed deactivations, Genesys permission errors, no Genesys events during
        business hours, the service stopping, or Genesys events piling up or failing to process.</p>
      <ol class="steps">
        <li>Under <b>Alarm emails</b>, type the address and click ${b('Add')}, then click ${b('Save')}.</li>
        <li>That person gets an email from <b>AWS Notifications</b> and clicks <b>Confirm subscription</b>. Until then their status
          says <b>Waiting for confirmation</b> and they receive nothing.</li>
      </ol>
      <p>${b('Remove')} then ${b('Save')} stops the emails. Use a shared address (a team alias) where you can, so alarms don't depend on one person.</p>
      <h3>Advanced</h3>
      <ul>
        <li><b>Don't enforce timeouts older than</b> (default 900 s = 15 min): after an outage, old timeouts are recorded as Expired instead of deactivating people late.</li>
        <li><b>Double-check the code on the conversation:</b> before acting, confirms the wrap-up code on the Genesys conversation itself. Leave on.</li>
        <li><b>If a deactivation's outcome is unknown:</b> what to do when Genesys errors mid-request. "Retry" waits until it's proven the request didn't land; "Mark failed" leaves it for a person.</li>
        <li><b>Report time zone:</b> the time zone used for days, weeks and months in ${go('reports', 'Reports')}.</li>
      </ul>`,
  },
  {
    id: 'timeouts',
    title: 'Looking up a timeout ("why was this agent deactivated?")',
    body: () => html`
      <ol class="steps">
        <li>Open ${go('timeouts', 'Timeouts')}. Choose a <b>Period</b>, and optionally an <b>Outcome</b>, <b>Agent ID</b> or <b>Queue ID</b>, then ${b('Apply')}.</li>
        <li>Click the row. The details show <b>Why</b> (the rule that decided), when it timed out, when the agent was deactivated and reactivated, how long they were off the queue, and the Genesys conversation ID.</li>
        <li>To check the interaction in Genesys, search for that conversation ID in Genesys interaction search.</li>
      </ol>
      <p>"Inferred" next to a deactivation means Genesys didn't answer the request cleanly, so the result was confirmed by checking the queue afterwards.</p>
      <p>For one agent's whole history, click their name anywhere it's a link (or <b>Agent report</b> in a timeout's details), then
        <b>All of this agent's timeouts</b> at the bottom of their page.</p>`,
  },
  {
    id: 'statuses',
    title: 'What each outcome means',
    body: () => {
      const rows: [string, string, string][] = [
        ['deactivated', 'Taken off the queue. Waiting for the agent to reactivate it.', 'Nothing. The agent reactivates it in Genesys.'],
        ['reactivated', 'The agent turned the queue back on. Time off the queue is recorded.', 'Nothing.'],
        ['already_inactive', 'The agent was already off that queue when the timeout happened.', 'Nothing.'],
        ['dry_run', 'Would have acted (see Why), but dry run is on.', 'Check it before enforcing the queue.'],
        ['detected', 'Recorded while the worker wasn\'t in Enforce mode.', 'Nothing. These are never enforced later.'],
        ['deactivation_pending', 'In progress, e.g. Genesys asked us to slow down.', 'Nothing. It resolves on its own within minutes.'],
        ['grace_warning', 'Below the grace count, so not enforced this time.', 'Nothing.'],
        ['outside_hours', 'Happened outside the enforcement hours.', 'Nothing.'],
        ['exempt_agent', 'The agent is exempt.', 'Nothing.'],
        ['ignored_queue', 'The queue is Off or not listed.', 'Add the queue if it should be enforced.'],
        ['coalesced', 'Part of an incident already handled, e.g. two interactions timing out together. Only one deactivation.', 'Nothing.'],
        ['expired', 'Processed too late to be fair (after an outage).', 'Nothing. Tell the owner if you see many.'],
        ['unconfirmed', 'Genesys shows a different wrap-up code on the conversation, so nothing was done.', 'Nothing. Mention it to the owner if frequent.'],
        ['not_member', 'The agent is no longer a member of the queue.', 'Nothing.'],
        ['queue_missing', 'The queue no longer exists in Genesys.', 'Remove it from Queues.'],
        ['membership_removed', 'The agent was removed from the queue while deactivated.', 'Nothing.'],
        ['failed', 'Something stopped it. The details say why.', 'Read the Note in the details, and tell the owner.'],
      ];
      return html`<div class="table-wrap"><table>
        <thead><tr><th>Outcome</th><th>Meaning</th><th>What to do</th></tr></thead>
        <tbody>${rows.map(([s, m, a]) => html`<tr><td class="nowrap">${statusPill(s)}</td><td>${m}</td><td>${a}</td></tr>`)}</tbody>
      </table></div>`;
    },
  },
  {
    id: 'agents',
    title: 'When an agent asks',
    body: () => html`
      <dl class="faq">
        <dt>"Why was I taken off the queue?"</dt>
        <dd>You didn't finish wrap-up within the queue's time limit, so Genesys assigned the timeout code. You're only off the queue where it happened.
          Look the agent up on ${go('timeouts', 'Timeouts')} to see the exact interaction and time.</dd>
        <dt>"How do I get back on?"</dt>
        <dd>Reactivate the queue yourself in Genesys Cloud, from your queue activation list. It's on purpose that you do it, not the system.
          A supervisor can also activate an agent on a queue in Genesys
          (<a href="https://help.genesys.cloud/articles/activate-agents-on-queues/" target="_blank" rel="noopener">Genesys: activate agents on queues</a>).</dd>
        <dt>"Am I off all my queues?"</dt>
        <dd>No. Only the queue where the timeout happened. Your status and other queues aren't touched.</dd>
        <dt>"I finished wrap-up in time."</dt>
        <dd>Open the timeout on ${go('timeouts', 'Timeouts')} and check the conversation ID in Genesys. The system only acts on the
          <code>ININ-WRAP-UP-TIMEOUT</code> code Genesys itself assigned, and double-checks it on the conversation.</dd>
        <dt>"Does it count against me?"</dt>
        <dd>Every timeout is recorded and appears in ${go('reports', 'Reports')} and on the agent's own report page, including ones that weren't enforced.</dd>
      </dl>`,
  },
  {
    id: 'reports',
    title: 'Reading the reports',
    body: () => html`
      <p class="lead">Pick a period at the top right (last 24 hours, 7, 30 or 90 days). Every number on the page covers that period.</p>
      <ul>
        <li><b>Timeouts</b> and <b>Change:</b> how many wrap-ups timed out, and the difference from the period before (red ▲ is more timeouts).
          Change shows once ACW Enforcer has data for the whole earlier period.</li>
        <li><b>Rate:</b> timeouts divided by all the wrap-ups ACW Enforcer saw for that agent or queue.</li>
        <li><b>Avg ACW:</b> average time in after-call work over every wrap-up. A timeout counts as the full time box (for example 2 minutes).</li>
        <li><b>Avg own wrap-up:</b> the same, counting only the wrap-ups the agent finished themselves.</li>
        <li><b>Close calls:</b> wrap-ups the agent finished themselves in the last 20 seconds before the limit. Agents with many close calls are likely to start timing out.</li>
        <li><b>ACW limit:</b> each queue's time box, learned from its timeouts (a timeout always lasts exactly the limit).</li>
        <li><b>ACW in timeouts:</b> total time spent in after-call work on wrap-ups that timed out.</li>
        <li><b>Timeouts by hour of day:</b> when in the day timeouts happen. Hover an hour for its wrap-ups and timeout rate.</li>
        <li><b>Deactivations</b> and <b>Avg inactive</b> (time from deactivation until the agent reactivated) appear once enforcement has deactivated someone.</li>
        <li><b>Repeat offenders:</b> agents with 2 or more timeouts in the last 30 days.</li>
      </ul>
      <p>In <b>By agent</b>, select a column heading to sort, or type in <b>Find an agent</b> to search by name. The first 100 agents are listed;
        ${b('Show all')} lists everyone. New agents show by name within a few minutes of their first wrap-up.</p>
      <p>Select an agent's name, anywhere it's a link, for their own page: the same numbers next to the team's, how long their wrap-ups take compared with
        everyone else, timeouts by day and hour, a breakdown by queue, and their latest timeouts.</p>
      <p>Days and hours use the Report time zone from ${go('rules', 'Rules')} › Advanced.</p>`,
  },
  {
    id: 'audit',
    title: 'The audit log',
    body: () => html`
      <p>${go('audit', 'Audit log')} lists every change to rules, queues, exemptions and users: who made it, when, and the old and new values.
        Emergency stops are marked. Nobody can edit or delete entries, so use it to answer "who changed this?"</p>`,
  },
  {
    id: 'users',
    title: 'Users and access',
    body: () => html`
      <p class="lead">Two things are needed to use ACW Enforcer: being allowed to sign in with company Okta, and being on the ${go('users', 'Users')} list.</p>
      <ol class="steps">
        <li>Ask IT (your Okta admin) to assign the person to the <b>ACW Enforcer</b> app in Okta.</li>
        <li>An admin opens ${go('users', 'Users')}, enters the email they sign in to Okta with under <b>Add a person</b>, picks a role, and clicks ${b('Add')}.</li>
      </ol>
      <div class="table-wrap"><table>
        <thead><tr><th>Role</th><th>Can</th></tr></thead>
        <tbody>
          <tr><td><b>Viewer</b></td><td>See every page. Change nothing.</td></tr>
          <tr><td><b>Admin</b></td><td>Everything a viewer can, plus change queues, rules and exemptions, use ${b('Emergency stop')}, and add, change or remove viewers and admins.</td></tr>
          <tr><td><b>Owner</b> (super admin)</td><td>Everything an admin can, plus make someone an owner, and change or remove other owners. Admins can't touch an owner's access.</td></tr>
        </tbody>
      </table></div>
      <ul>
        <li>Someone who can sign in but isn't on the list sees "no access".</li>
        <li>To change a role, use the role menu on their row. You can't change or remove yourself.</li>
        <li>There is always at least one owner: the last owner can't be removed or demoted. The system owner's access is set in the deployment, so it can't be lost from this page.</li>
        <li>Sign-in lasts about an hour, then you'll be asked to sign in again (usually instantly, through Okta).</li>
      </ul>`,
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    body: () => {
      const rows: [string, string, TemplateResult | string][] = [
        ['An agent timed out but wasn\'t deactivated', 'One of the rules said no.', html`Open the timeout on ${go('timeouts', 'Timeouts')}; its outcome and Why say which rule (${see('statuses', 'outcomes')}).`],
        ['Nobody is ever deactivated', 'Dry run, Observe-only, or the infrastructure switch is on.', 'Read the banner at the top of the Dashboard.'],
        ['Not getting alarm emails', "The address isn't on the list, or its confirmation email was never clicked.", html`Check ${go('rules', 'Rules')} › Alarm emails. If it says Waiting for confirmation, find the AWS Notifications email (check spam) and click Confirm subscription.`],
        ['Genesys card says "Not configured" or "Not signed in"', 'The Genesys sign-in details are missing or wrong.', 'Tell the system owner.'],
        ['Dashboard and Reports show all zeros', 'Worker mode is Capture, which only stores raw events.', html`Switch to Detect on ${go('rules', 'Rules')} › Enforcement. Nobody is deactivated in Detect.`],
        ['Event feed: no Genesys events for hours during work time', 'The Genesys EventBridge integration is off or misconfigured.', 'Ask the Genesys admin to check the "Amazon EventBridge Source - ACW Enforcer" integration is Active, then tell the system owner.'],
        ['Failed messages shows a number', 'Some Genesys events couldn\'t be processed.', 'Tell the system owner.'],
        ['Outcome "Failed" with a note about permission (403)', 'The Genesys OAuth client lacks a permission or division.', 'Ask the Genesys admin to check the "ACW Enforcer Worker" role and its divisions.'],
        ['"Viewer access is read-only"', 'You\'re a viewer.', 'Ask an admin to make you an admin on Users.'],
        ['"Signed in but has no access"', 'You\'re not on the Users list.', 'Ask an admin to add you.'],
        ['A queue doesn\'t show in the Add a queue search', 'The service can\'t see that queue\'s division in Genesys.', 'Ask the Genesys admin to add that division to the "ACW Enforcer Worker" role.'],
        ['An agent shows as a long ID instead of a name', 'The name hasn\'t been looked up yet, or the agent is in a division the service can\'t see.', 'Wait a few minutes. If it stays, ask the Genesys admin to add the agent\'s division to the "ACW Enforcer Worker" role.'],
        ['A queue\'s ACW limit is blank on Reports', 'That queue has had no timeouts in the last 30 days, so its limit can\'t be learned yet.', 'Nothing.'],
        ['The wrong queue is being enforced', '—', html`Use ${see('emergency', 'Emergency stop')} first, then fix the queue's mode.`],
      ];
      return html`<div class="table-wrap"><table>
        <thead><tr><th>You see</th><th>Likely cause</th><th>What to do</th></tr></thead>
        <tbody>${rows.map(([a, c, d]) => html`<tr><td><b>${a}</b></td><td>${c}</td><td>${d}</td></tr>`)}</tbody>
      </table></div>`;
    },
  },
  {
    id: 'owner',
    title: 'When to contact the system owner',
    body: () => html`
      <p>Most things can be done here. Contact the system owner (JD Vangsness) when:</p>
      <ul>
        <li>a card on the Dashboard isn't green (Genesys sign-in, event feed, failed messages);</li>
        <li>a timeout ends as <b>Failed</b> and the note doesn't explain it;</li>
        <li>you need live enforcement allowed in the infrastructure, or other technical changes.</li>
      </ul>`,
  },
];

let query = '';
let scrolledTo: string | null = null;

/** Every word of the query must appear in the section (title or rendered text). */
function matches(sec: Section, q: string): boolean {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const text = `${sec.title} ${document.getElementById(`g-${sec.id}`)?.textContent ?? ''}`.toLowerCase();
  return words.every((w) => text.includes(w));
}

export const guide: Page = {
  title: 'Guide',
  instant: true,
  async load() {
    /* static content */
  },
  view() {
    const target = app.routeParam;
    if (target !== scrolledTo) {
      scrolledTo = target;
      // After this render has been applied (not on every re-render, e.g. while typing a search).
      setTimeout(() => (target ? document.getElementById(`g-${target}`)?.scrollIntoView({ block: 'start' }) : window.scrollTo(0, 0)), 0);
    }
    const visible = SECTIONS.filter((s) => matches(s, query));
    return html`
      <div class="guide">
        <aside class="guide-toc">
          <input type="search" placeholder="Search the guide" .value=${query} aria-label="Search the guide"
            @input=${(e: Event) => { query = (e.target as HTMLInputElement).value; app.rerender(); }}>
          <nav aria-label="Guide contents">
            ${SECTIONS.map((s) => html`<a href="#/guide/${s.id}" class=${target === s.id ? 'active' : ''} ?hidden=${!visible.includes(s)}>${s.title}</a>`)}
          </nav>
          <button class="btn quiet compact" @click=${() => window.print()}>Print this guide</button>
        </aside>
        <article class="guide-body">
          <h1>ACW Enforcer guide</h1>
          <p class="hint">How to run ACW Enforcer day to day. Use the search box, or the <b>Help</b> link next to any page title to jump to its section.</p>
          ${SECTIONS.map((s) => html`<section id=${`g-${s.id}`} class="guide-section" ?hidden=${!visible.includes(s)}>
            <h2>${s.title} <a class="anchor" href="#/guide/${s.id}" aria-label="Link to this section">#</a></h2>${s.body()}</section>`)}
          ${visible.length === 0 ? html`<p class="muted">Nothing in the guide matches "${query}".</p>` : ''}
        </article>
      </div>`;
  },
};
